import { useEffect, useRef } from "react";

export function Starfield() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || typeof window.CanvasRenderingContext2D === "undefined") return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const root = canvas.closest<HTMLElement>(".community-landing");
    let seed = 17;
    const random = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    const colors = ["199,183,247", "185,221,242", "215,239,160", "247,244,239"];
    const glows = colors.map((color) => {
      const sprite = document.createElement("canvas");
      sprite.width = sprite.height = 64;
      const context = sprite.getContext("2d");
      if (context) {
        const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
        gradient.addColorStop(0, `rgba(${color},0.7)`);
        gradient.addColorStop(0.18, `rgba(${color},0.25)`);
        gradient.addColorStop(1, `rgba(${color},0)`);
        context.fillStyle = gradient;
        context.fillRect(0, 0, 64, 64);
      }
      return sprite;
    });
    const stars = Array.from({ length: 190 }, (_, i) => ({
      x: random(), y: random(), radius: 0.4 + random() * 1.25,
      speed: 0.035 + random() * 0.075, phase: random() * Math.PI * 2,
      drift: 1.5 + random() * 5, sway: 2 + random() * 7,
      color: colors[i % colors.length]!, sprite: glows[i % glows.length]!,
      sparkle: i % 15 === 0, glow: i % 9 === 0, trail: i % 61 === 0,
    }));
    let frame = 0;
    let width = 0;
    let height = 0;
    let elapsed = 0;
    let previous = 0;
    let lastDraw = -Infinity;
    const wrap = (value: number, range: number) => ((value % range) + range) % range;
    const render = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      const nextWidth = window.innerWidth;
      const nextHeight = window.innerHeight;
      if (width !== nextWidth || height !== nextHeight || canvas.width !== Math.round(nextWidth * ratio)) {
        width = nextWidth;
        height = nextHeight;
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
      }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);
      const seconds = motion.matches ? 0 : elapsed / 1000;
      const scroll = motion.matches ? 0 : window.scrollY;
      const count = width < 640 ? 85 : stars.length;
      for (let i = 0; i < count; i++) {
        const star = stars[i]!;
        const x = wrap(star.x * width + Math.sin(seconds * 0.12 + star.phase) * star.sway + seconds * star.drift * 0.35 + 24, width + 48) - 24;
        const y = wrap(star.y * height - scroll * star.speed - seconds * star.drift + 24, height + 48) - 24;
        const opacity = 0.2 + (Math.sin(star.phase + seconds * 0.7) + 1) * 0.2;
        if (star.glow) {
          const size = star.radius * 14;
          ctx.globalAlpha = 0.45 + opacity * 0.5;
          ctx.drawImage(star.sprite, x - size / 2, y - size / 2, size, size);
          ctx.globalAlpha = 1;
        }
        if (star.trail) {
          const gradient = ctx.createLinearGradient(x - 18, y + 30, x, y);
          gradient.addColorStop(0, `rgba(${star.color},0)`);
          gradient.addColorStop(1, `rgba(${star.color},${opacity * 0.45})`);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 0.65;
          ctx.beginPath();
          ctx.moveTo(x - 18, y + 30);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        ctx.fillStyle = `rgba(${star.color},${opacity})`;
        ctx.beginPath();
        if (star.sparkle) {
          const size = star.radius * (2.8 + Math.sin(seconds * 0.7 + star.phase) * 0.45);
          ctx.moveTo(x, y - size);
          ctx.quadraticCurveTo(x + 0.6, y - 0.6, x + size, y);
          ctx.quadraticCurveTo(x + 0.6, y + 0.6, x, y + size);
          ctx.quadraticCurveTo(x - 0.6, y + 0.6, x - size, y);
          ctx.quadraticCurveTo(x - 0.6, y - 0.6, x, y - size);
        } else {
          ctx.arc(x, y, star.radius, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      // Long, softly fading trails give the sky movement beyond the small drifting stars.
      if (!motion.matches) {
        for (let i = 0; i < 3; i++) {
          const life = wrap(seconds + i * 6, 19 + i * 3);
          const duration = 2.8;
          if (life > duration) continue;
          const progress = life / duration;
          const x = width * (0.35 + i * 0.23 - progress * 0.24);
          const y = height * (0.1 + i * 0.2 + progress * 0.2);
          const opacity = Math.sin(progress * Math.PI) * 0.38;
          const length = width < 640 ? 45 : 90;
          const trail = ctx.createLinearGradient(x + length, y - length * 0.6, x, y);
          trail.addColorStop(0, "rgba(185,221,242,0)");
          trail.addColorStop(1, `rgba(199,183,247,${opacity})`);
          ctx.strokeStyle = trail;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x + length, y - length * 0.6);
          ctx.lineTo(x, y);
          ctx.stroke();
          ctx.globalAlpha = opacity;
          ctx.drawImage(glows[1]!, x - 12, y - 12, 24, 24);
          ctx.globalAlpha = 1;
        }
      }
    };
    // One capped loop owns the cosmic motion. Hidden tabs stop it; reduced motion draws once.
    const tick = (now: number) => {
      frame = 0;
      if (document.hidden) return;
      if (previous) elapsed += Math.min(now - previous, 100);
      previous = now;
      if (motion.matches || now - lastDraw >= (window.innerWidth < 640 ? 1000 / 24 : 1000 / 30)) {
        render();
        lastDraw = now;
      }
      if (!motion.matches) frame = requestAnimationFrame(tick);
    };
    const schedule = () => {
      lastDraw = -Infinity;
      if (!frame && !document.hidden) { previous = 0; frame = requestAnimationFrame(tick); }
    };
    const onMotion = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      if (document.hidden) return;
      if (motion.matches) render();
      else schedule();
    };
    const onVisibility = () => {
      if (root) root.dataset.motionPaused = String(document.hidden);
      if (document.hidden) { cancelAnimationFrame(frame); frame = 0; previous = 0; }
      else schedule();
    };
    window.addEventListener("resize", schedule);
    document.addEventListener("visibilitychange", onVisibility);
    motion.addEventListener("change", onMotion);
    onVisibility();
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      document.removeEventListener("visibilitychange", onVisibility);
      motion.removeEventListener("change", onMotion);
      if (root) delete root.dataset.motionPaused;
    };
  }, []);
  return (
    <>
      <div className="community-cosmic-glows" aria-hidden="true"><span /><span /><span /></div>
      <canvas ref={ref} className="community-starfield" aria-hidden="true" />
    </>
  );
}
